import React, {useEffect, useState} from 'react';
import {ISupervisor} from "../employee-leave";
import {IEmployeeLeaveDetail, ILeaveBalance, ILeaveType} from "./leave-summary.types";
import {capitalizeString, getName, remainingLeave, removeUnderScoreWithLowerCase} from "../../../../utility/utility";
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    LineChartOutlined,
    UserOutlined
} from "@ant-design/icons";
import {Empty, Select} from "antd";
import LeavePeriodDropdown from "./component/leave-period-dropdown";
import server from "../../../../server";
import '../../../../styles/component/leave-summary.scss'
import EmployeeMonthlyLeaveChart from "./employee-monthly-leave-chart";

interface ISupervisorScreenProps {
    supervisor: ISupervisor;
    monthList: string[];
}

interface TotalLeaveTypeAnalysis {
    type: string;
    totalTaken: number;
}

interface ILeaveSummaryState {
    summary: {
        totalReportees: number;
        pendingRequest: number;
        approvedRequest: number;
        avgLeaveUsage: string;
    };
    employeeDetail: IEmployeeLeaveDetail[];
    totalLeaveDistribution: {
        totalLeaves: number,
        pieData: { labels: string[], data: number[] };
        leaveTypeAnalysis: TotalLeaveTypeAnalysis[];
    }
    employeeSelected: string | null;
    leavePeriodId: string
}

interface ProgressBarProps {
    data: {
        leavePercentage: number,
        totalApplied: number,
        totalAllotted: number
    }
    size?: 'small' | 'large';
}

const ProgressBar: React.FC<ProgressBarProps> = ({data, size}) => (
    <div className={`progress-bar ${size}`}>
        <div className="progress-title">
            <span className="label">Leave Balance</span>
            <span className="value">{data.totalApplied}/{data.totalAllotted} days</span>
        </div>

        <div className="progress-track">
            <div
                className={
                    "progress-fill " +
                    (data.leavePercentage > 80
                        ? "high"
                        : data.leavePercentage > 50
                            ? "medium"
                            : "low")
                }
                style={{width: `${data.leavePercentage}%`}}
            />
        </div>
    </div>
);

const SupervisorLeaveSummaryView = ({supervisor, monthList}: ISupervisorScreenProps) => {
    const [summaryData, setSummaryData] = useState<ILeaveSummaryState>({
        summary: {
            totalReportees: 0,
            pendingRequest: 0,
            approvedRequest: 0,
            avgLeaveUsage: '',
        },
        employeeDetail: [],
        totalLeaveDistribution: {
            totalLeaves: 0,
            pieData: {labels: [], data: []},
            leaveTypeAnalysis: [],
        },
        employeeSelected: null,
        leavePeriodId: ''
    });
    const [filteredEmployeeDetail, setFilteredEmployeeDetail] = useState<IEmployeeLeaveDetail[]>();
    const [selectedEmployee, setSelectedEmployee] = useState<string>('all');
    const [pagination, setPagination] = useState({
        pageNumber: 0,
        pageSize: 10
    })

    const employeeId = supervisor.empId;

    useEffect(() => {
        (async () => {
            if (summaryData.leavePeriodId)
                await leaveBalance(employeeId);
        })();
    }, [employeeId, summaryData?.leavePeriodId]);

    function handleLeavePeriodId(val: string) {
        setSummaryData((prev) => ({
            ...prev,
            leavePeriodId: val
        }));
    }

    async function leaveBalance(employeeId: string) {
        const payload = {
            employeeId: employeeId,
            leavePeriodId: summaryData.leavePeriodId,
            includeReportees: true
        }
        const response = await server.leave.balance.search(payload, pagination.pageNumber, pagination.pageSize);
        processData(response);
    }

    function processData(res: ILeaveBalance[]) {
        const totalReportee = res.length;
        let totalApproved = 0, totalPending = 0, averageLeave = 0;
        const employeeDetails: IEmployeeLeaveDetail[] = [];
        const leaveTotals = new Map<string, number>();

        res.forEach((item: ILeaveBalance) => {
            employeeDetails.push({
                id: item.employeeId,
                name: getName(item.employeeName.firstName, '', item.employeeName.lastName),
                employeeName:item.employeeName,
                email: item.email,
                profilePhotoThumbnailId:item.profilePhotoThumbnailId,
                allottedLeave: item.unit,
                availableBalance: item.unit - remainingLeave(item),
                totalApplied: item.pending + item.approved,
                leaveType: getLeaveTypeData(item.leaves),
                monthLeaveData: item.leaves
            });

            // Accumulate counts for pie chart
            item.leaves.forEach(l => {
                leaveTotals.set(
                    l.type,
                    (leaveTotals.get(l.type) || 0) + (l.applied ?? 0)
                );
            });
        });

        totalApproved = employeeDetails.reduce(
            (sum, emp) =>
                sum +
                emp.leaveType.reduce((innerSum, lt) => innerSum + (lt.approved ?? 0), 0),
            0
        );
        totalPending = employeeDetails.reduce(
            (sum, emp) =>
                sum +
                emp.leaveType.reduce((innerSum, lt) => innerSum + (lt.pending ?? 0), 0),
            0
        );
        const validEmployees = employeeDetails.filter(emp => emp.allottedLeave > 0);

        averageLeave = Math.round(
            validEmployees.reduce((sum, emp) => {
                const percentUsed = (emp.totalApplied / emp.allottedLeave) * 100;
                return sum + percentUsed;
            }, 0) / validEmployees.length
        );

        const pieData = {
            labels: Array.from(leaveTotals.keys()),
            data: Array.from(leaveTotals.values())
        };

        const totalLeaveTypeAnalysis = getTeamLeaveAnalysis(res);

        setSummaryData((prev) => ({
            ...prev,
            summary: {
                totalReportees: totalReportee,
                pendingRequest: totalPending,
                approvedRequest: totalApproved,
                avgLeaveUsage: `${averageLeave}%`
            },
            employeeDetail: employeeDetails,
            totalLeaveDistribution: {
                totalLeaves: 90,
                pieData: pieData,
                leaveTypeAnalysis: totalLeaveTypeAnalysis
            },
            employeeSelected: null
        }));
        setFilteredEmployeeDetail(employeeDetails);
    }

    function getTeamLeaveAnalysis(res: ILeaveBalance[]) {
        const mapper = new Map<string, number>();
        res.forEach((item: ILeaveBalance) => {
            item.leaves.forEach((l: ILeaveType) => {
                const key = removeUnderScoreWithLowerCase(l.type);
                const currentValue = mapper.get(key) || 0;
                const applied = l.applied ?? 0;

                mapper.set(key, currentValue + applied);
            });
        });

        return Array.from(mapper.entries()).map(([type, totalTaken]) => ({
            type: type,
            totalTaken: totalTaken,
        }));
    }

    function getLeaveTypeData(leaves: ILeaveType[]) {
        const leaveData = leaves.map((l) => {
            return {
                leaveType: removeUnderScoreWithLowerCase(l.type),
                allotted: l.unit,
                balance: l.balance,
                taken: l.unit - (l.pending + l.approved),
                pending: l.pending,
                rejected: l.rejected,
                cancelled: l.cancelled,
                approved: l.approved
            }
        });
        return leaveData ?? [];
    }


    const showDetails = (id: string) => {
        return summaryData?.employeeSelected === id;
    }

    function handleClick(id: string) {
        setSummaryData(prev =>
            prev
                ? {
                    ...prev,
                    employeeSelected: prev.employeeSelected === id ? null : id
                }
                : prev
        );
    }

    // const chartData = useMemo(() => {
    //     if (!summaryData?.totalLeaveDistribution.pieData) return {labels: [], datasets: []};
    //     return {
    //         labels: summaryData.totalLeaveDistribution.pieData.labels,
    //         datasets: [{
    //             label: "Total",
    //             data: summaryData?.totalLeaveDistribution.pieData.data,
    //             backgroundColor: [
    //                 'rgba(255, 26, 104, 0.2)',
    //                 'rgba(54, 162, 235, 0.2)',
    //                 'rgba(255, 206, 86, 0.2)',
    //                 'rgba(255, 159, 64, 0.2)',
    //                 'rgba(153, 102, 255, 0.2)',
    //                 'rgba(75, 192, 192, 0.2)',
    //                 'rgba(0, 0, 0, 0.2)'
    //             ],
    //             borderColor: [
    //                 'rgba(255, 26, 104, 1)',
    //                 'rgba(54, 162, 235, 1)',
    //                 'rgba(255, 206, 86, 1)',
    //                 'rgba(75, 192, 192, 1)',
    //                 'rgba(153, 102, 255, 1)',
    //                 'rgba(255, 159, 64, 1)',
    //                 'rgba(0, 0, 0, 1)'
    //             ],
    //             borderWidth: 1,
    //             hoverOffset: 10,
    //         }]
    //     };
    // }, [summaryData?.totalLeaveDistribution.pieData]);

    const renderCardItems = [
        {
            key: 'totalReportee',
            icon: <UserOutlined className="totalReportee"/>,
            label: "Total Reportees",
            value: summaryData?.summary?.totalReportees ?? 0,
        },
        {
            key: 'pending',
            icon: <ClockCircleOutlined className="pending"/>,
            label: "Pending Requests",
            value: Math.ceil(summaryData?.summary?.pendingRequest ?? 0),
        },
        {
            key: 'approved',
            icon: <CheckCircleOutlined className="approved"/>,
            label: "Approved Requests",
            value: Math.ceil(summaryData?.summary?.pendingRequest ?? 0),
        },
        {
            key: 'avg',
            icon: <LineChartOutlined className="avg"/>,
            label: "Avg Leave Usage",
            value: summaryData?.summary?.avgLeaveUsage ?? 0,
        }
    ];

    function handleSearch(searchText: string) {
        if (!summaryData) return;

        const allEmployees = summaryData.employeeDetail;

        if (!searchText.trim()) {
            setFilteredEmployeeDetail(allEmployees);
            return;
        }
        const filtered = allEmployees.filter(emp =>
            emp.name.toLowerCase().includes(searchText.toLowerCase())
        );
        setFilteredEmployeeDetail(filtered);
    }

    function handleSelect(value: string) {
        if (value)
            setSelectedEmployee(value);
        if (value !== 'all') {
            setFilteredEmployeeDetail(summaryData?.employeeDetail?.filter((item) => item.id === value));
        } else {
            setFilteredEmployeeDetail(summaryData?.employeeDetail)
        }
    }

    const selectOptions = () => {
        if (!summaryData) return [];

        return [
            {value: 'all', label: 'All Employees'},
            ...summaryData.employeeDetail.map(emp => ({
                value: emp.id,
                label: emp.name
            }))
        ];
    }

    return (
        <div className="leave-summary-supervisor">
            <LeavePeriodDropdown setLeavePeriod={handleLeavePeriodId}/>
            {/* SUMMARY CARDS */}
            <div className="leave-summary-cards">
                {renderCardItems.map((item, idx) => (
                    <div className={`card-item ${item.key}`} key={idx}>
                        <span className="title"><span className="icon">{item.icon}</span>{item.label}</span>
                        <span className="value">{item.value}</span>
                    </div>
                ))}
            </div>

            {/* LEAVE TYPE ANALYSIS */}
            <div className="leave-analysis">
                {/*<h3 className="leave-analysis__title">*/}
                {/*    Team Leave Distribution by Leave Type*/}
                {/*</h3>*/}
                <div className="leave-analysis__content">

                    {/*<div className="leave-analysis__chart">*/}
                    {/*    <Pie data={chartData} width={100} height={100}/>*/}
                    {/*</div>*/}
                    {/*<div className="leave-analysis__list">*/}
                    {/*    <h3>Team Leave</h3>*/}
                    {/*    {summaryData?.totalLeaveDistribution.leaveTypeAnalysis?.map((l, idx) => (*/}
                    {/*        <div className="leave-analysis__item" key={idx}>*/}
                    {/*            <span className="type">{l.type}</span>*/}
                    {/*            <span className="units">{l.totalTaken}</span>*/}
                    {/*        </div>*/}
                    {/*    ))}*/}
                    {/*</div>*/}


                    {/* EMPLOYEE LEAVE TRACKER */}
                    <div className="employee-leave-summary">
                        <div className={'employee-leave-tracker-header'}>
                            <h3>Employee Leave Tracker</h3>
                            <Select
                                className={'dropdown'}
                                showSearch={true}
                                value={selectedEmployee}
                                options={selectOptions()}
                                onSelect={handleSelect}
                                onSearch={handleSearch}
                            />
                        </div>

                        <div className="item-list">
                            {filteredEmployeeDetail && filteredEmployeeDetail.length > 0 ? filteredEmployeeDetail.map((item) => {
                                const data = {
                                    leavePercentage: Math.ceil((item.totalApplied / item.allottedLeave) * 100),
                                    totalApplied: item.totalApplied,
                                    totalAllotted: item.allottedLeave
                                }

                                return (
                                    <div className="item" key={item.id}>
                                        <div className={"item-heading"}>
                                            <h4 className="text-name">{item.name}</h4>
                                            <span className="link" onClick={() => handleClick(item.id)}>
                                      {showDetails(item.id) ? 'Less' : 'Details'}
                                </span>
                                        </div>

                                        <ProgressBar data={data}/>

                                        <div className="employee-leave-stats">
                                            <div className="employee-leave-stats__item total-allotted">
                                                <span className="label">Total Units</span>
                                                <span className="value">{item.allottedLeave}</span>
                                            </div>

                                            <div className="employee-leave-stats__item total-applied">
                                                <span className="label">Total Applied</span>
                                                <span className="value">{item.totalApplied}</span>
                                            </div>

                                            <div className="employee-leave-stats__item total-available">
                                                <span className="label">Total Available</span>
                                                <span className="value">{item.availableBalance}</span>
                                            </div>
                                        </div>

                                        {showDetails(item.id) &&

                                        <div className={'leave-type-details-list'}>
                                            <div className={'leave-type-details-item'}>
                                                <div className="details-wrapper">
                                                    <div className="leave-type-items">
                                                        {item.leaveType.map((l, idx) => {
                                                            const percentage = Math.round(((l.approved + l.pending) / l.allotted) * 100);
                                                            const data = {
                                                                leavePercentage: percentage,
                                                                totalApplied: l.approved + l.pending,
                                                                totalAllotted: l.allotted,
                                                            };
                                                            return (
                                                                <div key={idx} className="card">
                                                                    <div className="title">{l.leaveType}</div>
                                                                    <ProgressBar data={data} size={'small'}/>

                                                                    <div className="leave-status">
                                                                        <div className="leave-status__item pending">
                                                                            <span className="label">Pending</span>
                                                                            <span className="value">{l.pending}</span>
                                                                        </div>

                                                                        <div className="leave-status__item approved">
                                                                            <span className="label">Approved</span>
                                                                            <span
                                                                                className="value">{l.approved}</span>
                                                                        </div>

                                                                        <div className="leave-status__item cancelled">
                                                                            <span className="label">Cancelled</span>
                                                                            <span
                                                                                className="value">{l.cancelled}</span>
                                                                        </div>

                                                                        <div className="leave-status__item rejected">
                                                                            <span className="label">Rejected</span>
                                                                            <span
                                                                                className="value">{l.rejected}</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                                <div className="month-chart-wrapper">
                                                    <h4 className={'title'}>Month tracker</h4>
                                                    <div className={'chart'}>
                                                     <EmployeeMonthlyLeaveChart leaves={item.monthLeaveData}/>
                                                    </div>

                                                </div>
                                            </div>
                                        </div>
                                        }

                                    </div>
                                );
                            }) : (
                                <Empty/>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SupervisorLeaveSummaryView;
